// The operator console's chassis. Sibling of `_shell.tsx` and pathless for the
// same reason: it contributes no URL segment, so every child keeps the exact
// `fullPath` it would have had, and `frontend/tests/routes.test.ts` needs no
// loosening for a layout route.
//
// It is a SEPARATE layout because `_shell` is org-scoped end to end — it
// resolves an `orgSlug` from the path or search and sends a mismatch to
// `/no-access`. The console is cross-tenant and names no org at all.
//
// WHY `denied` GOES TO THE CALLER'S OWN DASHBOARD, not `/no-access`. A
// signed-in non-staff visitor who types `/admin` should land exactly where a
// mistyped URL would take them. `/no-access` is a distinguishable destination,
// so redirecting there would confirm that `/admin` is a real surface rather
// than a typo. The backend's 403 is the real enforcement; this is the client
// half of not advertising the door.

import { Navigate, Outlet, createRoute, useMatches, useRouterState } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { AppShell } from "../shell/AppShell";
import { adminNav, hrefs } from "../shell/hrefs";
import { signOut, useMe } from "../screens/overview";
import { decideStaffAccess } from "../screens/admin/api";
import { rememberNext } from "../screens/signin/nextUrl";

export const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_admin",
  component: AdminLayout,
});

function AdminLayout() {
  const me = useMe();
  const matches = useMatches();
  const href = useRouterState({ select: (state) => state.location.href });

  const title = matches
    .map((match) => match.staticData?.title)
    .filter((value): value is string => Boolean(value))
    .pop();

  // `checking` still renders the body, matching ShellGate: each screen shows
  // its own loading state, and a shell-level skeleton would remount the body
  // on every arrival.
  if (me.isPending) {
    return (
      <AppShell nav={adminNav("")} title={title} onSignOut={signOut}>
        <Outlet />
      </AppShell>
    );
  }

  const access = decideStaffAccess(me.data);
  if (access === "signed-out") {
    rememberNext(href);
    return <Navigate to="/signin" replace />;
  }
  if (access === "denied") {
    return <Navigate to={hrefs(me.data!.org_slug).overview} replace />;
  }

  return (
    <AppShell nav={adminNav(me.data!.email)} title={title} onSignOut={signOut}>
      <Outlet />
    </AppShell>
  );
}
